"use client";

import { CountryCode, FlagComponent } from "@/components/ui/phone-input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useGlobals } from "@/features/globals/api/use-globals";
import {
  localeNames,
  locales,
  usePathname,
  useRouter,
} from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { IconCheck, IconWorld } from "@tabler/icons-react";
import { useLocale } from "next-intl";
import { useParams } from "next/navigation";
import { useCallback, useMemo, useState, useTransition } from "react";

export const LanguageSwitcher = ({
  className,
  triggerClassName,
}: {
  className?: string;
  triggerClassName?: string;
}) => {
  const pathname = usePathname();
  const currentLocale = useLocale();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const params = useParams();

  const { data: globals } = useGlobals();
  const languagePicker = globals?.languagePicker;

  const [isOpen, setIsOpen] = useState(false);

  const onSelectChange = useCallback(
    (locale: string) => {
      // const nextLocale = event.target.value as Locale;
      startTransition(async () => {
        await fetch("/api/language", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ locale }),
        });

        router.replace(
          // @ts-expect-error -- TypeScript will validate that only known `params`
          // are used in combination with a given `pathname`. Since the two will
          // always match for the current route, we can skip runtime checks.
          { pathname, params },
          { locale: locale }
        );
      });
    },
    [pathname, params, router]
  );

  const handleLanguageSelect = useCallback(
    (locale: string) => {
      if (locale === currentLocale || isPending) return;

      setIsOpen(false);
      // Small delay to ensure popover closes before language change
      setTimeout(() => {
        onSelectChange(locale);
      }, 50);
    },
    [currentLocale, isPending, onSelectChange]
  );

  return useMemo(
    () =>
      languagePicker ? (
        <Popover open={isOpen && !isPending} onOpenChange={setIsOpen}>
          <PopoverTrigger>
            <div
              className={cn(
                "flex items-center space-x-2 text-current pl-3 pr-4 py-2 hover:bg-brand-grey5/20 rounded-lg transition-colors duration-200",
                {
                  "opacity-50 cursor-not-allowed": isPending,
                },
                triggerClassName
              )}
            >
              <IconWorld className="h-6 w-6" strokeWidth={1} />
              <span>{currentLocale.toUpperCase()}</span>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              "w-fit bg-brand-green2-300 border-transparent p-2",
              className
            )}
            align="center"
            side="bottom"
            sideOffset={12}
          >
            <div className="flex flex-col space-y-3">
              {locales.map((locale) => {
                const isCurrent = locale === currentLocale;
                const isDisabled = isCurrent || isPending;

                return (
                  <button
                    key={locale}
                    type="button"
                    className={cn(
                      "flex items-center space-x-2 text-left p-2 rounded hover:bg-brand-green1-300/20 transition-colors",
                      {
                        "text-primary": isCurrent,
                        "pointer-events-none": isDisabled,
                        "opacity-50": isDisabled,
                      }
                    )}
                    onClick={() => handleLanguageSelect(locale)}
                    disabled={isDisabled}
                  >
                    <div className="flex items-center space-x-2 text-brand-white">
                      <FlagComponent
                        country={locale.toUpperCase() as CountryCode}
                        countryName={locale}
                        className="h-6 w-6 rounded-none bg-transparent"
                      />
                      <span className="text-sm">
                        {localeNames[locale as keyof typeof localeNames]}
                      </span>
                    </div>
                    {isCurrent ? (
                      <IconCheck className="h-4 w-4 [&>path]:stroke-brand-white" />
                    ) : (
                      <div className="h-4 w-4" />
                    )}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <div className="mx-1" />
      ),
    [
      currentLocale,
      isOpen,
      triggerClassName,
      className,
      languagePicker,
      handleLanguageSelect,
      isPending,
    ]
  );
};
